import { Close, Maximize, Minimize } from '@mint-lib/icons';
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerPortal,
  DrawerRoot,
  DrawerTitle,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import { DownloadButton } from '../DownloadReport/DownloadButton.jsx';
import PdfViewer from './PdfViewer.jsx';
import {
  useReportPreviewData,
  useReportPreviewModal,
} from './useReportPreview.jsx';

type Props = {
  isMobile?: boolean;
  isAgentic?: boolean;
  isAgenticFullScreen?: boolean;
};

const ReportPreview = ({
  isMobile = false,
  isAgentic = false,
  isAgenticFullScreen = false,
}: Props) => {
  const { open, onClose, isFullScreen, toggleFullScreen } =
    useReportPreviewModal();
  const { reportData, downloadReport } = useReportPreviewData();

  if (!open) return null;

  if (isAgentic && !isAgenticFullScreen && !isFullScreen) {
    return (
      <div
        className={cn('z-dialog h-full w-full')}
        aria-label={'agentic-report-preview'}
      >
        <div className="grid h-full min-h-0 min-w-0 flex-1 grid-rows-[auto_1fr] overflow-x-auto bg-ui-primary">
          <div className="flex w-full flex-row justify-between">
            <div className="flex h-15 flex-row items-center justify-center gap-1">
              <Button
                icon
                size={'large'}
                variant={'text'}
                onClick={onClose}
                aria-label="agentic-report-preview-close"
              >
                <Close />
              </Button>
              <div
                className={typographyVariants({
                  variant: 'h6',
                })}
              >
                {reportData.fileName}
              </div>
            </div>

            <div
              className="flex flex-row items-center gap-1"
              aria-label="agentic-report-preview-tools"
            >
              <DownloadButton
                handleDownloadFile={downloadReport}
                variant="text"
                size="large"
              />
              <Button
                icon
                size={'large'}
                variant={'text'}
                onClick={toggleFullScreen}
                aria-label="agentic-report-preview-expander"
              >
                {isFullScreen ? <Minimize /> : <Maximize />}
              </Button>
            </div>
          </div>

          <div className="flex min-w-0 overflow-x-auto overflow-y-auto">
            <PdfViewer fileUrl={reportData.blobUrl} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <Drawer open={open}>
      <DrawerPortal>
        <DrawerRoot
          aria-label="report-preview"
          aria-describedby={undefined}
          size="50%"
          side={isMobile ? 'bottom' : 'right'}
          onOpenAutoFocus={(e) => e.preventDefault()}
          style={{
            height: isMobile ? 'calc(100dvh - 64px)' : '100dvh',
            width: '100dvw',
            borderTopLeftRadius: isMobile ? 0 : undefined,
            borderTopRightRadius: isMobile ? 0 : undefined,
          }}
        >
          <DrawerHeader onClose={onClose}>
            <div className="flex w-full justify-between">
              <div className="flex flex-row gap-1">
                <DrawerTitle>{reportData.fileName}</DrawerTitle>
                {(!isMobile || isAgentic) && !isFullScreen && (
                  <DownloadButton
                    handleDownloadFile={downloadReport}
                    variant={'text'}
                  />
                )}
              </div>
              {!isMobile && isAgentic && !isAgenticFullScreen && (
                <div
                  className="flex flex-row gap-1"
                  aria-label="report-preview-tools"
                >
                  <DownloadButton
                    handleDownloadFile={downloadReport}
                    variant={'text'}
                  />
                  <Button
                    icon
                    size={'small'}
                    variant={'text'}
                    onClick={toggleFullScreen}
                    aria-label="report-preview-expander"
                  >
                    {isFullScreen ? <Minimize /> : <Maximize />}
                  </Button>
                </div>
              )}
            </div>
          </DrawerHeader>
          <DrawerContent className="flex h-screen justify-center overflow-x-auto overflow-y-auto p-0">
            <div className="flex overflow-x-auto overflow-y-auto">
              <PdfViewer fileUrl={reportData.blobUrl} isMobile={isMobile} />
            </div>
          </DrawerContent>
        </DrawerRoot>
      </DrawerPortal>
    </Drawer>
  );
};

export { ReportPreview };
